import { serviceTokenStore, userTokenStore } from './serviceTokenStore';

/**
 * API Fetch Utility
 * All API requests use the service account token (no user access token).
 */

/**
 * API request options
 * @typedef {Object} ApiOptions
 * @property {string} url - API endpoint URL
 * @property {string} [method='GET'] - HTTP method
 * @property {any} [body] - Request body
 * @property {Object} [headers] - Additional headers
 * @property {Object} [params] - Query parameters
 * @property {boolean} [useTimeout=false] - Whether to use timeout
 */

/**
 * Make an API request using the service account token
 * @param {ApiOptions} options - Request options
 * @returns {Promise<Response>} Fetch response
 */
export async function apiFetch(options) {
    const {
        url,
        method = 'GET',
        body,
        headers = {},
        params,
        useTimeout = false
    } = options;

    const accessToken = userTokenStore.getToken() ?? serviceTokenStore.getToken();

    if (!accessToken && !headers['Authorization']) {
        throw new Error('Service token not available - please wait for the app to load');
    }

    // Build URL with query parameters
    let fullUrl = url;
    const baseUrl = process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_BASE_URL?.replace(/=+$/, '') ||
        '';

    if (baseUrl && !url.startsWith('http')) {
        fullUrl = `${baseUrl}${url}`;
    }

    if (params) {
        const queryString = new URLSearchParams(params).toString();
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString;
    }

    // Build headers — omit Content-Type for FormData so the browser sets it with the multipart boundary
    const isFormData = body instanceof FormData;
    const requestHeaders = new Headers({
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        'Authorization': `Bearer ${accessToken}`,
        ...headers
    });
    const locale = localStorage.getItem('app-language') || 'en';
    if (locale) {
        requestHeaders.append('Accept-Language', locale);
    }

    // Set up timeout if needed
    let controller = null;
    let timeoutId = null;

    if (useTimeout) {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), 10000);
    }

    try {

        const response = await fetch(fullUrl, {
            method,
            headers: requestHeaders,
            body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
            // body: body,
            signal: controller?.signal
        });

        if (timeoutId) clearTimeout(timeoutId);

        return response;
    } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        console.error(`[API] Error in request to ${fullUrl}:`, error);
        throw error;
    }
}
